import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, PackageOpen } from 'lucide-react';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import BackButton from '../components/BackButton';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminServiceReview() {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/assets/pending`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setAssets(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id, action, body) => {
    setBusy(b => ({ ...b, [id]: action }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/assets/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      setAssets(prev => prev.filter(a => a.asset_id !== id));
    } finally {
      setBusy(b => ({ ...b, [id]: null }));
    }
  };

  const confirmReject = async () => {
    if (rejectReason.trim().length < 5) return;
    const id = rejectingId;
    setRejectingId(null);
    await act(id, 'reject', { reason: rejectReason.trim() });
    setRejectReason('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />
      <div className="container mx-auto px-4 max-w-5xl py-8">
        <BackButton to="/admin/dashboard" label={t('admin.back2Dashboard')} className="mb-4" />
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-[#111827] mb-1">{t('adminServiceReview.title', 'Servicios pendientes de aprobación')}</h1>
        <p className="text-gray-500 mb-6">{t('adminServiceReview.pendingCount', '{{count}} servicio(s) esperando revisión', { count: assets.length })}</p>

        {loading ? (
          <p className="text-gray-400 py-12 text-center">{t('common.loading', 'Cargando…')}</p>
        ) : assets.length === 0 ? (
          <div className="flex flex-col items-center text-center py-16 text-gray-400">
            <PackageOpen className="w-14 h-14 mb-3" />
            <p>{t('adminServiceReview.empty', 'No hay servicios pendientes. ¡Todo al día!')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {assets.map(a => {
              const b = busy[a.asset_id];
              return (
                <div key={a.asset_id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col sm:flex-row gap-4">
                  <img
                    src={a.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                    alt={a.name}
                    className="w-full sm:w-44 h-32 object-cover rounded-xl shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">{a.type?.replace('_', ' ')}</span>
                      <span className="text-xs text-gray-400">• {a.location_zone}</span>
                    </div>
                    <h3 className="font-bold text-lg text-gray-900">{a.name}</h3>
                    <p className="text-sm text-gray-500 line-clamp-2">{a.description}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      <span className="font-bold text-[#134168]">{formatDOP(a.price_per_day)}{t('listing.perDayShort2', '/día')}</span>
                      {a.provider_email && <span className="text-xs text-gray-400">{t('adminServiceReview.byProvider', 'por {{email}}', { email: a.provider_email })}</span>}
                    </div>
                  </div>
                  <div className="flex sm:flex-col gap-2 justify-center shrink-0">
                    <button onClick={() => act(a.asset_id, 'approve')} disabled={!!b}
                      className="inline-flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-sm hover:bg-green-600 disabled:opacity-50">
                      {b === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} {t('adminServiceReview.approveBtn', 'Aprobar')}
                    </button>
                    <button onClick={() => { setRejectingId(a.asset_id); setRejectReason(''); }} disabled={!!b}
                      className="inline-flex items-center justify-center gap-2 bg-white border border-red-200 text-red-600 px-4 py-2 rounded-full font-bold text-sm hover:bg-red-50 disabled:opacity-50">
                      {b === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} {t('adminServiceReview.rejectBtn', 'Rechazar')}
                    </button>
                  </div>
                  {rejectingId === a.asset_id && (
                    <div className="w-full sm:w-72 shrink-0 bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-medium text-gray-600 mb-2">{t('adminServiceReview.rejectReasonLabel', 'Motivo del rechazo (lo verá el proveedor)')}</p>
                      <textarea
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        rows={2}
                        className="w-full text-sm border border-gray-200 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-red-200"
                        placeholder={t('adminServiceReview.rejectReasonPlaceholder', 'Ej. fotos de baja calidad, descripción incompleta...')}
                      />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => setRejectingId(null)} className="text-xs px-3 py-1.5 rounded-full border border-gray-200">
                          {t('common.cancel', 'Cancelar')}
                        </button>
                        <button
                          onClick={confirmReject}
                          disabled={rejectReason.trim().length < 5}
                          className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white font-bold disabled:opacity-40"
                        >
                          {t('adminServiceReview.rejectConfirmBtn', 'Confirmar rechazo')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
